import { usePopupRef } from '@framework/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faFolderOpen, faFolderTree, faPen, faPlus, faUser } from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';

type Props = {
  onEdit(): void;
  onCopy(): void;
  showConfigureIndividualPermissions: boolean;
  onConfigureIndividualPermissions(): void;
  onMoveViewModel(): void;
  onMoveToOtherWorkspace(): void;
  onDelete(): void;
};

export const MenuButtons: React.FC<Props> = ({
  onDelete,
  onEdit,
  onCopy,
  showConfigureIndividualPermissions,
  onConfigureIndividualPermissions,
  onMoveViewModel,
  onMoveToOtherWorkspace,
}: Props) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleClose, handleOpen } = usePopupRef<HTMLDivElement>(false);

  const handleDelete = () => {
    onDelete();
    handleClose();
  };

  const handleCopy = () => {
    onCopy();
    handleClose();
  };

  const handleConfigureIndividualPermissions = () => {
    onConfigureIndividualPermissions();
    handleClose();
  };

  const handleMoveViewModel = () => {
    onMoveViewModel();
    handleClose();
  };

  const handleMoveToOtherWorkspace = () => {
    onMoveToOtherWorkspace();
    handleClose();
  };

  return (
    <div className="relative">
      <div className="inline-flex text-sm">
        <button
          className="whitespace-nowrap rounded-l bg-gray-500 py-1 pl-3 pr-2 font-bold text-white hover:bg-gray-700"
          onClick={onEdit}
        >
          <FontAwesomeIcon icon={faPen} className="mr-1" />
          {t('common.action.edit')}
        </button>

        <button className="rounded-r bg-gray-500 px-2 py-1 font-bold text-white hover:bg-gray-700" onClick={handleOpen}>
          <FontAwesomeIcon icon={faEllipsisV} />
        </button>
      </div>

      <div
        className={'absolute z-10 w-56 rounded-lg bg-white py-1 shadow-lg ' + (isOpen ? 'block' : 'hidden')}
        ref={popupRef}
      >
        <button
          className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
          onClick={handleCopy}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faCopy} />
          </span>
          <span className="text-sm">{t('viewModel.copy.label')}</span>
        </button>

        {showConfigureIndividualPermissions && (
          <button
            className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
            onClick={handleConfigureIndividualPermissions}
          >
            <span className="relative inline-block w-6">
              <span className="">
                <FontAwesomeIcon icon={faUser} />
              </span>
              <span className="absolute right-0 top-0 inline-block size-2 -translate-x-1 text-xs">
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </span>
            <span className="text-sm">{t('viewModel.addOrRemoveMembers.label')}</span>
          </button>
        )}

        <button
          className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
          onClick={handleMoveViewModel}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faFolderOpen} />
          </span>
          <span className="text-sm">{t('workspace.viewModelMenu.move')}</span>
        </button>

        <button
          className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
          onClick={handleMoveToOtherWorkspace}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faFolderTree} />
          </span>
          <span className="text-sm">{t('workspace.moveToAnother.label')}</span>
        </button>

        <button
          className="block w-full px-2 py-1 text-left text-red-700 hover:bg-indigo-500 hover:text-white"
          onClick={handleDelete}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faTrashAlt} />
          </span>
          <span className="text-sm">{t('common.action.delete')}</span>
        </button>
      </div>
    </div>
  );
};
